import { useCallback, useEffect, useRef, useState } from 'react';
import { catalogService } from '../../../services/catalogService';
import { preOrderService } from '../../../services/preOrderService';
import type { CatalogCartItem, CatalogViewItem, PreOrder } from '../../../types/catalog';

const SAVE_DEBOUNCE_MS = 600;

interface UseCatalogCartProps {
  catalogId: number;
}

interface CartProduct {
  productName?: string;
  productCode?: string;
  unitPrice: number;
}

export interface CartLine extends CartProduct {
  productId: number;
  quantity: number;
}

const errorMessage =(e: unknown, fallback: string): string => {
  const data = (e as { response?: { data?: unknown } }).response?.data;
  return typeof data === 'string' && data ? data : fallback;
};

/**
 * Server-persisted cart with optimistic UI: quantity changes apply locally at once and the
 * full desired cart is PUT after a short debounce (or immediately before checkout).
 */
export function useCatalogCart({ catalogId }: UseCatalogCartProps) {
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  // Name/code/price of cart products — the catalog is paged, so the current page may not contain every cart row
  const [products, setProducts] = useState<Record<number, CartProduct>>({});
  // Cart rows whose product is gone from the catalog — shown so the user can remove them
  const [unavailableItems, setUnavailableItems] = useState<CatalogCartItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const latest = useRef<Record<number, number>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirty = useRef(false);
  // Serialize PUTs so an older response never lands after a newer request
  const chain = useRef<Promise<unknown>>(Promise.resolve());

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const cart = await catalogService.getCart(catalogId);
        if (cancelled) return;
        const initial: Record<number, number> = {};
        const initialProducts: Record<number, CartProduct> = {};
        cart.items.filter((item) => item.available).forEach((item) => {
          initial[item.productId] = item.quantity;
          if (item.unitPrice !== null) {
            initialProducts[item.productId] = {
              productName: item.productName,
              productCode: item.productCode,
              unitPrice: item.unitPrice,
            };
          }
        });
        latest.current = initial;
        setQuantities(initial);
        setProducts(initialProducts);
        setUnavailableItems(cart.items.filter((item) => !item.available));
      } catch (e) {
        console.error('Failed to load cart:', e);
        if (!cancelled) {
          setError(errorMessage(e, 'Не вдалося завантажити кошик'));
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [catalogId]);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const flush = useCallback(async (): Promise<boolean> => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (!dirty.current) {
      await chain.current;
      return true;
    }

    dirty.current = false;
    const items = Object.entries(latest.current).map(([productId, quantity]) => ({
      productId: Number(productId),
      quantity,
    }));

    setSaving(true);
    const request = chain.current.then(() => catalogService.saveCart(catalogId, items));
    chain.current = request.catch(() => undefined);
    try {
      await request;
      setError(null);
      return true;
    } catch (e) {
      dirty.current = true;
      setError(errorMessage(e, 'Не вдалося зберегти кошик'));
      return false;
    } finally {
      setSaving(false);
    }
  }, [catalogId]);

  const setQuantity = (item: CatalogViewItem, quantity: number) => {
    const { productId } = item;
    const next = { ...latest.current };
    if (quantity > 0) {
      next[productId] = quantity;
    } else {
      delete next[productId];
    }
    latest.current = next;
    dirty.current = true;
    setQuantities(next);
    setProducts((prev) => ({
      ...prev,
      [productId]: {
        productName: item.productName,
        productCode: item.productCode,
        unitPrice: item.unitPrice,
      },
    }));
    setUnavailableItems((prev) => prev.filter((item) => item.productId !== productId));

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      flush();
    }, SAVE_DEBOUNCE_MS);
  };

  const checkout = async (phone: string): Promise<PreOrder | null> => {
    setCheckingOut(true);
    try {
      if (!(await flush())) return null;
      const preOrder = await preOrderService.checkout(catalogId, phone);
      latest.current = {};
      setQuantities({});
      return preOrder;
    } catch (e) {
      setError(errorMessage(e, 'Не вдалося оформити замовлення'));
      return null;
    } finally {
      setCheckingOut(false);
    }
  };

  // Everything currently in the cart, for the order summary
  const lines: CartLine[] = Object.entries(quantities)
    .filter(([productId]) => products[Number(productId)])
    .map(([productId, quantity]) => ({
      productId: Number(productId),
      quantity,
      ...products[Number(productId)],
    }))
    .sort((a, b) => (a.productName ?? '').localeCompare(b.productName ?? ''));

  const total = lines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0);

  return {
    data: {
      quantities,
      lines,
      unavailableItems,
      total,
      error,
    },
    state: {
      saving,
      checkingOut,
    },
    actions: {
      setQuantity,
      checkout,
    },
  };
}
